/**
 * useUsage Hook
 *
 * Custom hook for tracking user's query usage and limits.
 * Provides usage data, percentage calculation, and warning thresholds.
 *
 * Features:
 * - Fetches current usage and limits from backend
 * - Auto-refreshes usage data periodically
 * - Calculates usage percentage
 * - Provides warning states (80%, 95%, 100%)
 * - Error handling and loading states
 */

import { useState, useEffect, useCallback } from 'react';
import { billingService } from '../services/billing';
import type { UsageMetrics } from '../types/billing';

export interface UsageData {
  usage: number;
  limit: number;
  percentage: number;
  isUnlimited: boolean;
  isWarning: boolean;      // >= 80%
  isCritical: boolean;     // >= 95%
  isLimitReached: boolean; // >= 100%
  storageUsed: number;
  storageLimit: number;
  storagePercentage: number;
}

export interface UseUsageReturn extends UsageData {
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const calculatePercentage = (used: number, limit: number): number => {
  if (limit === -1) return 0; // Unlimited
  if (limit === 0) return 100; // No limit means 100%
  return Math.min((used / limit) * 100, 100);
};

/**
 * Hook to track and monitor usage metrics
 *
 * @param enabled - Whether to fetch usage data (default: true) - set to false for unauthenticated users
 * @param refreshInterval - Interval in ms to refresh (default: 60000 = 1 minute)
 * @returns Usage data, loading state, error state, and refresh function
 */
export function useUsage(enabled = true, refreshInterval = 60000): UseUsageReturn {
  const [usage, setUsage] = useState<UsageMetrics | null>(null);
  const [loading, setLoading] = useState(enabled); // Only show loading if enabled
  const [error, setError] = useState<string | null>(null);

  const fetchUsage = useCallback(async () => {
    if (!enabled) return; // Skip fetch if not enabled

    try {
      setError(null);
      const data = await billingService.getUsageMetrics();
      setUsage(data);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to load usage data';
      setError(errorMessage);
      console.error('Error fetching usage:', err);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  // Initial fetch
  useEffect(() => {
    if (!enabled) {
      setLoading(false); // Immediately set loading to false if disabled
      return;
    }
    fetchUsage();
  }, [fetchUsage, enabled]);

  // Auto-refresh
  useEffect(() => {
    if (!enabled) return; // Skip auto-refresh if not enabled

    const interval = setInterval(() => {
      fetchUsage();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [enabled, refreshInterval, fetchUsage]);

  // Calculate usage data
  const usageData: UsageData = usage ? {
    usage: usage.queries_used,
    limit: usage.queries_limit,
    percentage: calculatePercentage(usage.queries_used, usage.queries_limit),
    isUnlimited: usage.queries_limit === -1,
    isWarning: false,
    isCritical: false,
    isLimitReached: false,
    storageUsed: usage.storage_used,
    storageLimit: usage.storage_limit,
    storagePercentage: calculatePercentage(usage.storage_used, usage.storage_limit),
  } : {
    usage: 0,
    limit: 0,
    percentage: 0,
    isUnlimited: false,
    isWarning: false,
    isCritical: false,
    isLimitReached: false,
    storageUsed: 0,
    storageLimit: 0,
    storagePercentage: 0,
  };

  // Set warning flags only if not unlimited
  if (!usageData.isUnlimited) {
    usageData.isWarning = usageData.percentage >= 80 && usageData.percentage < 95;
    usageData.isCritical = usageData.percentage >= 95 && usageData.percentage < 100;
    usageData.isLimitReached = usageData.percentage >= 100;
  }

  return {
    ...usageData,
    loading,
    error,
    refresh: fetchUsage,
  };
}

export default useUsage;
